/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
/**
 * Utility functions for team-related operations
 */
import { Debate } from '../types';

// Type definition for user object
type User = {
  id: string;
  username?: string;
  email?: string;
};

/**
 * Gets the team name for a given team ID
 */
export const getTeamName = (teamId: string | undefined, debate: Debate | null): string => {
  // Handle null or undefined inputs
  if (!teamId || !debate || !debate.teams) {
    return "Unknown Team";
  }
  
  // Convert ID to string for safer comparisons
  const teamIdStr = String(teamId);
  
  // Find the team in the debate by ID
  const team = debate.teams.find(t => String(t.id) === teamIdStr);
  
  // Return the team name if found
  if (team) {
    return team.name || "Unnamed Team";
  }
  
  // If we couldn't find by ID, try using position (Team 1 or Team 2)
  if (debate.teams.length > 0) {
    if (String(debate.teams[0].id) === teamIdStr) {
      return debate.teams[0].name || "Team 1";
    } else if (debate.teams.length > 1 && String(debate.teams[1].id) === teamIdStr) {
      return debate.teams[1].name || "Team 2";
    }
  }
  
  return "Unknown Team";
};

/**
 * Gets the user name for a given user ID, with special handling for the current user
 */
export const getUserName = (
  senderId: string | undefined, 
  debate: Debate | null, 
  currentUser: User | null
): string => {
  // Handle null or undefined inputs
  if (!senderId) {
    return currentUser?.username || "Anonymous";
  }
  
  // Convert sender ID to string for safer comparisons
  const senderIdStr = String(senderId);
  
  // Handle special case for messages from a team
  if (senderIdStr.startsWith('team_')) {
    const teamId = senderIdStr.replace('team_', '');
    return getTeamName(teamId, debate);
  }
  
  // Check if sender is the current user
  if (currentUser && String(currentUser.id) === senderIdStr) {
    // Use the actual username instead of "You"
    return currentUser.username || "You";
  }
  
  // Handle case with no debate data
  if (!debate) {
    return senderId || "Unknown User";
  }
  
  // Try to find the team associated with this user ID
  const userTeam = debate.teams.find(team => 
    team.userId && String(team.userId) === senderIdStr
  );
  
  if (userTeam) {
    return userTeam.name || "Team Member";
  }
  
  // If no match found, return shortened ID
  return senderIdStr.substring(0, 8) + '...';
}; 